import type {
  CubeHellAction,
  CubeHellState,
  CubeHellViewerState,
} from "@cloudnest/cube-hell-engine";
import {
  filterStateForViewer,
  validateAndReduceGameState,
} from "@cloudnest/cube-hell-engine";
import { cubeHellEventToAction } from "./event-adapter";
import type { CubeHellReplayFixtureEvent } from "./replay-fixtures.generated";

/**
 * UI-free 큐브지옥 (cube-hell) replay simulation. `init` seeds canonical state;
 * every other event is reconstructed via the event adapter and folded through the
 * engine reducer. Fails loudly on unsupported/rejected events.
 *
 * Hidden information: each snapshot carries the canonical `state` (for the recap
 * toggle) and an observer `viewer` projection where both arrangements are masked
 * (null) and the PRNG state is dropped. The public query/response log is kept.
 */
export interface CubeHellReplaySnapshot {
  readonly index: number;
  readonly event: CubeHellReplayFixtureEvent;
  readonly action: CubeHellAction | null;
  /**
   * Full unmasked canonical state (arrangements + rng) — kept for the recap
   * toggle. WARNING: only safe in local replay contexts; never ship this to a
   * client. The masked `viewer` projection is the client-safe shape.
   */
  readonly state: CubeHellState;
  readonly viewer: CubeHellViewerState;
}

function requireState(
  state: CubeHellState | null,
  eventType: string,
): CubeHellState {
  if (state === null) throw new Error(`${eventType} before init`);
  return state;
}

function readInitState(payload: Record<string, unknown>): CubeHellState {
  const candidate = payload.state;
  if (
    typeof candidate !== "object" ||
    candidate === null ||
    !("arrangements" in candidate) ||
    !("players" in candidate) ||
    !("log" in candidate) ||
    !("queryCounts" in candidate)
  ) {
    throw new Error("cube-hell init event is missing a valid state snapshot");
  }
  return candidate as CubeHellState;
}

function snapshot(
  index: number,
  event: CubeHellReplayFixtureEvent,
  action: CubeHellAction | null,
  state: CubeHellState,
): CubeHellReplaySnapshot {
  return {
    index,
    event,
    action,
    state,
    viewer: filterStateForViewer(state, null),
  };
}

export function buildCubeHellReplaySnapshots(
  events: readonly CubeHellReplayFixtureEvent[],
): readonly CubeHellReplaySnapshot[] {
  const snapshots: CubeHellReplaySnapshot[] = [];
  let state: CubeHellState | null = null;

  for (const event of events) {
    if (event.eventType === "init") {
      state = readInitState(event.payload);
      snapshots.push(snapshot(snapshots.length, event, null, state));
      continue;
    }

    const current = requireState(state, event.eventType);
    const action = cubeHellEventToAction(event.eventType, event.payload);
    const result = validateAndReduceGameState(current, action);
    if (!result.accepted) {
      throw new Error(
        `${event.eventType} (#${event.sequenceNumber}) rejected: ${
          result.error ?? "Validation failed"
        }`,
      );
    }
    state = result.state;
    snapshots.push(snapshot(snapshots.length, event, action, state));
  }

  if (state === null) {
    throw new Error("Cannot replay an empty cube-hell event log");
  }
  return snapshots;
}
